import { useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom-v5-compat";
import { FlightShopSelectors } from "@b2bportal/core-flights";
import {
  PACKAGES_QUERY_PARAM,
  PackageSearchSelectors,
  PackageType,
} from "@b2bportal/core-packages";
import type { FlightFareSelectionFunction } from "@b2bportal/core-types";
import { ensureExhaustive, getPackageRoutes } from "@b2bportal/core-utilities";
import { datadogRum } from "@datadog/browser-rum";
import { useReturnPageContainer } from "@tenant/flights";
import type { AppDispatch } from "@/store";
import { PackageFlightSelectors } from "@/store/packages/selectors";
import { PackagesBaseActions } from "@/store/packages/slice/packages";

export const usePackageFlightReturnPageContainer = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const [queryParams] = useSearchParams();

  const packagesByFareId = useSelector(
    PackageFlightSelectors.getPackageStateByFareId
  );

  const tripDetailsById = useSelector(
    FlightShopSelectors.tripDetailsByIdSelector
  );

  // FlightCard calls onFareSelect and goToNextStep in the same click handler.
  // The store is updated at once, but useSelector values are only refreshed
  // on the next render, after the handler returns. Reading the selected fare
  // from a selector inside goToNextStep gives the previous render's value
  // (the departure fare, since returnFareId is not set yet), and its package
  // token carries the default return flight instead of the clicked one
  // (RBC-2496). Keep the clicked fare in a ref, same as the departure page.
  const lastSelectedFareRef = useRef<{ fareId: string; tripId: string }>();

  const packageType = useSelector(
    PackageSearchSelectors.getSearchFormPackageType
  );

  // TODO(Nate): This is similar to what's in PackageHotelShopPage, will look into a shared hook
  const goToNextStep = useCallback(() => {
    switch (packageType) {
      case PackageType.HOTEL_FLIGHT_CAR:
      case PackageType.FLIGHT_CAR:
        navigate(
          `${getPackageRoutes().FULL_ROUTES.CARS.AVAILABILITY}?${queryParams.toString()}`
        );
        break;
      case PackageType.HOTEL_FLIGHT:
        navigate(
          `${getPackageRoutes().FULL_ROUTES.CHECKOUT}?${queryParams.toString()}`
        );
        break;
      case PackageType.HOTEL_CAR:
        // Shouldn't ever hit here if in hotels, kick back to search/homepage if so.
        navigate(getPackageRoutes().BASE_ROUTE);
        break;
      default:
        ensureExhaustive(packageType);
    }
  }, [navigate, packageType, queryParams]);

  const onReturnFlightSelection = useCallback(() => {
    const selected = lastSelectedFareRef.current;
    if (!selected) return;

    const packageToken = packagesByFareId?.[selected.fareId];
    if (!packageToken) {
      // Without a package for the fare we can't continue. Report it rather
      // than leave a silent dead "Continue" button.
      datadogRum.addError(
        new Error("No package found for selected return fare"),
        {
          feature: "package-flight-return",
          fareId: selected.fareId,
          tripId: selected.tripId,
          hasPackageFlightData: packagesByFareId != null,
        }
      );
      return;
    }

    dispatch(PackagesBaseActions.setCurrentActivePackage(packageToken));
    queryParams.set(
      PACKAGES_QUERY_PARAM.packageToken,
      packageToken.token.value
    );

    // Cancellation policy of the clicked return fare:
    // fareDetails.slices[0].amenitiesUtas.utas.cancellationPolicy
    const fareDetails = tripDetailsById?.[selected.tripId]?.fareDetails.find(
      (fare) => fare.id === selected.fareId
    );
    const cancellationPolicy =
      fareDetails?.slices?.[0]?.amenitiesUtas?.utas?.cancellationPolicy;

    if (cancellationPolicy) {
      dispatch(
        PackagesBaseActions.setSelectedFlightCancellationPolicy(
          cancellationPolicy
        )
      );
    }

    goToNextStep();
  }, [tripDetailsById, packagesByFareId, dispatch, queryParams, goToNextStep]);

  // Get package-aware filtered flight list instead of core filtered list
  const packageFilteredFlightList = useSelector(
    PackageFlightSelectors.getPackageFilteredAndSortedReturnFlightList
  );

  const containerProps = useReturnPageContainer({
    goToNextStep: onReturnFlightSelection,
  });

  const { handleFareSelect: coreHandleFareSelect } = containerProps.handlers;
  const handleFareSelect: FlightFareSelectionFunction = useCallback(
    (params) => {
      lastSelectedFareRef.current = {
        fareId: params.fareId,
        tripId: params.tripId,
      };
      return coreHandleFareSelect(params);
    },
    [coreHandleFareSelect]
  );

  // Override fare selection and flight list with package-aware versions
  return {
    ...containerProps,
    handlers: {
      ...containerProps.handlers,
      handleFareSelect,
    },
    context: {
      ...containerProps.context,
      flightList: packageFilteredFlightList,
    },
  };
};
